import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Typography,
  Autocomplete,
  Chip,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { Locale } from '../../../services/workout.service';
import { MedicationOptionsResponseDto } from '../../../services/admin-medication.service';
import { fileService } from '../../../services/file.service';
import { getImageUrl } from '../../../utils/image-url.util';

interface MedicationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  initialData?: any;
  options: MedicationOptionsResponseDto;
  isLoading: boolean;
  title: string;
  submitLabel: string;
}

const locales: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

// Common dosages for suggestions
const commonDosages: string[] = [
  '25mg', '50mg', '100mg', '200mg', '250mg', '500mg', '1000mg',
  '400iu', '1000iu', '2000iu', '5000iu',
  '5ml', '10ml', '15ml',
  '1 tab', '2 tab'
];

export const MedicationDialog: React.FC<MedicationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  options,
  isLoading,
  title,
  submitLabel,
}) => {
  const [nameI18n, setNameI18n] = useState<Partial<Record<Locale, string>>>({});
  const [detailsI18n, setDetailsI18n] = useState<Partial<Record<Locale, string>>>({});
  const [form, setForm] = useState<string>('');
  const [dosage, setDosage] = useState<string[]>([]);
  const [externalLink, setExternalLink] = useState<string>('');

  // Image
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageId, setImageId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Tabs for description
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setNameI18n(initialData.nameI18n || {});
        setDetailsI18n(initialData.detailsI18n || {});
        setForm(initialData.form || '');
        setDosage(initialData.dosage || []);
        setExternalLink(initialData.externalLink || '');

        // Image
        setImageId(initialData.imageId || null);
        setPreviewUrl(initialData.imageUrl ? getImageUrl(initialData.imageUrl) : null);
      } else {
        const initialNames: Partial<Record<Locale, string>> = {};
        const initialDetails: Partial<Record<Locale, string>> = {};
        locales.forEach(loc => { 
          initialNames[loc] = ''; 
          initialDetails[loc] = '';
        });
        setNameI18n(initialNames);
        setDetailsI18n(initialDetails);
        setForm(options.forms.length > 0 ? options.forms[0].value : '');
        setDosage([]);
        setExternalLink('');

        setImageId(null);
        setPreviewUrl(null);
      }
      setSelectedFile(null);
      setActiveTab(0);
    }
  }, [open, initialData, options]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    let finalImageId = imageId;

    if (selectedFile) {
      setIsUploading(true);
      try {
        const uploadedFile = await fileService.uploadFile(selectedFile, 'Medication image');
        finalImageId = uploadedFile.id;
      } catch (error) {
        console.error('Failed to upload image:', error);
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    onSubmit({
      nameI18n,
      detailsI18n,
      form,
      dosage,
      externalLink: externalLink || null,
      imageId: finalImageId,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            {/* Left Column: Translations & Image */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Зображення</Typography>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="medication-image-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="medication-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Завантажити фото
                  </Button>
                </label>
                
                {previewUrl && (
                  <Box sx={{ 
                    border: '1px dashed #ccc', 
                    borderRadius: 1, 
                    p: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5',
                    position: 'relative'
                  }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain' 
                      }}
                    />
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={() => {
                        setPreviewUrl(null);
                        setSelectedFile(null);
                        setImageId(null);
                      }}
                      sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      Видалити
                    </Button>
                  </Box>
                )}
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Переклади назви</Typography>
              {locales.map(lang => (
                <TextField
                  key={lang}
                  margin="dense"
                  label={`Назва (${lang.toUpperCase()})`}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nameI18n[lang] || ''}
                  onChange={e => setNameI18n({ ...nameI18n, [lang]: e.target.value })}
                  required={lang === 'uk'}
                  size="small"
                />
              ))}
            </Grid>

            {/* Right Column: Properties */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Властивості</Typography>
              
              <FormControl fullWidth margin="dense">
                <InputLabel>Форма випуску</InputLabel>
                <Select
                  value={form}
                  label="Форма випуску"
                  onChange={e => setForm(e.target.value)}
                >
                  {options.forms.map(f => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={commonDosages}
                  value={dosage}
                  onChange={(_event, newValue) => {
                    setDosage(newValue);
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Дозування"
                      placeholder="Введіть дозування та натисніть Enter"
                      helperText="Наприклад: 10mg, 20mg, Forte"
                    />
                  )}
                />
              </Box>

              <TextField
                margin="dense"
                label="Зовнішнє посилання"
                type="url"
                fullWidth
                value={externalLink}
                onChange={e => setExternalLink(e.target.value)}
                placeholder="https://..."
              />
            </Grid>

            {/* Description Section */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Опис / Інструкція</Typography>
              <Tabs 
                value={activeTab} 
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                {locales.map((lang) => (
                  <Tab key={lang} label={lang.toUpperCase()} />
                ))}
              </Tabs>
              {locales.map((lang, index) => (
                <div key={lang} role="tabpanel" hidden={activeTab !== index}>
                  {activeTab === index && (
                    <TextField
                      label={`Опис (${lang.toUpperCase()})`}
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                      value={detailsI18n[lang] || ''}
                      onChange={e => setDetailsI18n({ ...detailsI18n, [lang]: e.target.value })}
                      placeholder="Введіть детальний опис, рецепт або інструкцію..."
                    />
                  )}
                </div>
              ))}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button onClick={handleSubmit} disabled={isLoading || isUploading} variant="contained">
          {isLoading || isUploading ? <CircularProgress size={24} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
